import { useMutation, useQueryClient } from "@tanstack/vue-query";
import camelize from "camelcase-keys";
import snakeize from "snakecase-keys";
import { FetchError } from "ofetch";
import { useDonorAccessToken } from "./composables";
import { authQueries, type Preference } from "./queries";
import { memberQueries } from "@/domains/member/queries";
import { organizationQueries } from "@/domains/organization/queries";

export class UpdateDonorAvatarError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UpdateDonorAvatarError";
  }
}
export function useUpdateDonorAvatarMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { blob: Blob | null }) => {
      const client = useApiClient();
      if (!params.blob) throw new Error("Error preprocessing image");
      const formData = new FormData();
      formData.append("image", params.blob);
      try {
        await client("/donor/auth/avatar", {
          method: "POST",
          body: formData,
        });
      } catch (e: unknown) {
        if (e instanceof FetchError) {
          if (e.response?.status === 413) {
            throw new UpdateDonorAvatarError("Image is too large");
          }
        }
        throw e;
      }
    },
    onSuccess() {
      return queryClient.invalidateQueries(authQueries.self());
    },
  });
}

export function useLoginByPaymentClaimMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { resourceId: string }) => {
      const client = useApiClient();
      const response = await client<LoginByPaymentClaimResponse>("/donor/auth/loginByPaymentClaim", {
        method: "POST",
        body: snakeize(data, { deep: true }),
      });

      return camelize(response, { deep: true });
    },
    onSuccess: (data) => {
      const token = useDonorAccessToken();
      token.value = data.session.token;

      return Promise.all([
        queryClient.invalidateQueries({
          ...authQueries.self(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: authQueries.preferences().queryKey,
        }),
        queryClient.invalidateQueries({ queryKey: memberQueries.all() }),
      ]);
    },
  });
}

export function useRequestLoginCodeMutation() {
  return useMutation({
    mutationFn: async (data: { email: string }) => {
      const client = useApiClient();
      await client("/donor/auth/requestLoginCode", {
        method: "POST",
        body: snakeize(data, { deep: true }),
      });
    },
  });
}

export function useLoginWithCodeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { email: string; code: string }) => {
      const client = useApiClient();
      try {
        const response = await client<LoginWithCodeResponse>("/donor/auth/loginWithCode", {
          method: "POST",
          body: snakeize(data, { deep: true }),
        });

        return camelize(response, { deep: true });
      } catch (e) {
        if (e instanceof FetchError) {
          if (e.response?.status === 400) {
            throw new Error("Invalid code");
          }
        }
        throw e;
      }
    },
    onSuccess: (data) => {
      const token = useDonorAccessToken();
      token.value = data.session.id;

      return Promise.all([
        queryClient.invalidateQueries({
          ...authQueries.self(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: authQueries.preferences().queryKey,
        }),
        queryClient.invalidateQueries({ queryKey: memberQueries.all() }),
      ]);
    },
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const token = useDonorAccessToken();
      token.value = null;

      const posthog = usePosthog();
      posthog?.reset();

      return await Promise.all([
        queryClient.invalidateQueries({
          queryKey: authQueries.self().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: authQueries.preferences().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: organizationQueries.all(),
        }),
        queryClient.removeQueries({
          queryKey: memberQueries.all(),
        }),
      ]);
    },
  });
}

export function useSavePreferenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { key: string; value: string }) => {
      // Update the preferences in the cache
      queryClient.setQueryData<Preference[]>(["preferences"], (data) => {
        if (!data) return data;

        const preference = data.find((p) => p.key === params.key);

        if (preference) {
          return data.map((p) => {
            if (p.key === params.key) {
              return { ...p, value: params.value };
            }

            return p;
          });
        } else {
          return [...data, { key: params.key, value: params.value }];
        }
      });

      // Save the preferences on the server
      const client = useApiClient();
      await client("/donor/auth/preferences/save", {
        method: "POST",
        body: snakeize(params, { deep: true }),
      });
    },
  });
}

type SignUpErrorCode = "DONOR_ALREADY_EXISTS" | "ALREADY_MEMBER_OF_ORGANIZATION";

export class SignUpError extends Error {
  constructor(
    message: string,
    public readonly code: SignUpErrorCode
  ) {
    super(message);
    this.name = "SignUpError";
  }
}

type SignUpParams =
  | {
      isLoggedIn: true;
      organizationUrl: string;
    }
  | {
      isLoggedIn: false;
      email: string;
      name: string;
      organizationUrl: string;
    };

export function useSignUpMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ isLoggedIn: _, ...params }: SignUpParams) => {
      const client = useApiClient();

      try {
        const response = await client<SignUpResponse>("/donor/auth/signup", {
          method: "POST",
          body: snakeize(params, { deep: true }),
        });

        return camelize(response, { deep: true });
      } catch (e) {
        if (e instanceof FetchError) {
          if (e.response?.status === 400) {
            const { code, error } = camelize(
              e.response._data as {
                code: SignUpErrorCode;
                error: string;
              },
              { deep: true }
            );
            throw new SignUpError(error, code);
          }
        }
        throw e;
      }
    },
    onSuccess: async (data) => {
      if (data.session) {
        const token = useDonorAccessToken();
        token.value = data.session.token;
      }

      return await Promise.all([
        queryClient.invalidateQueries({
          ...authQueries.self(),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: authQueries.preferences().queryKey,
        }),
        queryClient.invalidateQueries({ queryKey: memberQueries.all() }),
      ]);
    },
  });
}

type LoginByPaymentClaimResponse = {
  donor: {
    id: string;
    email: string;
    name: string;
  };
  session: {
    id: string;
    token: string;
    expires_at: string;
  };
};

type LoginWithCodeResponse = {
  donor: {
    id: string;
    email: string;
    name: string;
  };
  session: {
    id: string;
    expire_at: string;
    created_at: string;
  };
};

type SignUpResponse = {
  donor?: {
    id: string;
    email: string;
    name: string;
  };
  session?: {
    id: string;
    token: string;
    expires_at: string;
  };
};
